import * as T from 'three';

/** Sparse, tapered peripheral strokes. Draw once after the world/post pass so
 * depth partitions and reflection cameras cannot duplicate the screen effect. */
export class FlightSpeedLines {
  readonly mesh:T.Mesh<T.InstancedBufferGeometry,T.ShaderMaterial>;
  strength=0;
  private readonly scene=new T.Scene();
  private readonly camera=new T.Camera();
  private readonly travel=new T.Vector3();
  private readonly inverse=new T.Quaternion();
  constructor(){
    const geometry=new T.InstancedBufferGeometry(),quad=new T.PlaneGeometry(2,1);
    geometry.index=quad.index;geometry.attributes=quad.attributes;geometry.instanceCount=24;
    const seeds=new Float32Array(24*3);
    for(let i=0;i<24;i++){seeds[i*3]=i/24*Math.PI*2+.14*Math.sin(i*7.1);seeds[i*3+1]=(i*.61803398875)%1;seeds[i*3+2]=(i*.38196601125+.17)%1;}
    geometry.setAttribute('seed',new T.InstancedBufferAttribute(seeds,3));
    const material=new T.ShaderMaterial({transparent:true,depthWrite:false,depthTest:false,toneMapped:false,
      uniforms:{strength:{value:0},time:{value:0},aspect:{value:1},center:{value:new T.Vector2()}},
      vertexShader:`attribute vec3 seed;uniform float time,strength,aspect;uniform vec2 center;
        varying vec2 vUv;varying float vFade;
        void main(){vUv=uv;float cycle=fract(time*(.65+seed.z*.25)+seed.y);
          vec2 ray=vec2(cos(seed.x),sin(seed.x));ray/=max(abs(ray.x),abs(ray.y));
          vec2 side=normalize(vec2(-ray.y,ray.x));side.x/=aspect;
          float radius=.67+cycle*.8;
          float len=.07+.15*strength+seed.z*.06;
          vec2 p=center+ray*(radius-uv.y*len)+side*position.x*(.0011+seed.z*.0007);
          vFade=smoothstep(0.,.18,cycle)*(1.-smoothstep(.70,1.,cycle))*(.5+.5*seed.z);
          gl_Position=vec4(p,0.,1.);}`,
      fragmentShader:`uniform float strength;varying vec2 vUv;varying float vFade;
        void main(){float edge=pow(max(0.,1.-abs(vUv.x*2.-1.)),1.5);
          float taper=sin(vUv.y*3.14159265);float a=edge*taper*vFade*strength*.27;
          gl_FragColor=vec4(.80,.89,.96,a);}`,
    });
    this.mesh=new T.Mesh(geometry,material);this.mesh.name='flight-boost-speed-lines';this.mesh.frustumCulled=false;this.mesh.visible=false;this.scene.add(this.mesh);
  }
  update(dt:number,active:boolean,speed:number,power:number,camera:T.PerspectiveCamera,velocity:T.Vector3,wallRunning=false):void{
    if(!active){this.strength=0;this.mesh.visible=false;return;}
    const h=T.MathUtils.clamp(Number.isFinite(dt)?dt:0,0,.1);
    const effort=wallRunning?.8:T.MathUtils.clamp(power,0,1);
    const target=effort*T.MathUtils.smoothstep(speed,5,16);
    this.strength+=(target-this.strength)*(1-Math.exp(-(target>this.strength?7:12)*h));
    const uniforms=this.mesh.material.uniforms;
    uniforms.time.value+=h;uniforms.strength.value=this.strength;uniforms.aspect.value=camera.aspect;
    this.inverse.copy(camera.quaternion).invert();this.travel.copy(velocity).applyQuaternion(this.inverse);
    const z=Math.max(8,-this.travel.z),center=uniforms.center.value as T.Vector2;
    center.set(T.MathUtils.clamp(this.travel.x/z*.2,-.12,.12),T.MathUtils.clamp(this.travel.y/z*.2,-.10,.10));
    this.mesh.visible=this.strength>.003;
  }
  render(renderer:T.WebGLRenderer):void{
    if(!this.mesh.visible)return;
    const autoClear=renderer.autoClear;renderer.autoClear=false;
    try{renderer.render(this.scene,this.camera);}finally{renderer.autoClear=autoClear;}
  }
  dispose():void{this.mesh.geometry.dispose();this.mesh.material.dispose();this.scene.clear();}
}
